"use client";
import * as React from 'react';
import type { Preceptor } from '@/types/preceptor';

interface SimpleFiltersProps {
  specialtyId?: string;
  state?: string;
  city?: string;
  preceptors?: Preceptor[];
  onFilterChange: (specialtyId: string, state: string, city: string) => void;
  /** 'card' (default) = bordered card with labels; 'inline' = compact bare selects for headers. */
  variant?: 'card' | 'inline';
}

export function SimpleFilters({ specialtyId = '', state = '', city = '', preceptors = [], onFilterChange, variant = 'card' }: SimpleFiltersProps) {
  const [specialtyIdValue, setSpecialtyIdValue] = React.useState(specialtyId);
  const [stateValue, setStateValue] = React.useState(state);
  const [cityValue, setCityValue] = React.useState(city);

  // Check if preceptors are still loading (empty array on initial render)
  const isLoading = preceptors.length === 0;

  // Extract available specialties with IDs from actual preceptor data
  const availableSpecialties = React.useMemo(() => {
    const specialtyMap = new Map<number, string>();
    preceptors.forEach(p => {
      // Extract from specialties array (preferred - has IDs)
      if (p.specialties?.length) {
        p.specialties.forEach(s => {
          if (s.id && s.name?.trim()) {
            specialtyMap.set(Number(s.id), s.name.trim());
          }
        });
      }
    });
    const uniqueSpecialties = Array.from(specialtyMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return uniqueSpecialties;
  }, [preceptors]);

  // Extract available states from actual preceptor data
  const availableStates = React.useMemo(() => {
    let filteredPreceptors = preceptors;
    
    // Filter by specialty ID if one is selected
    if (specialtyIdValue) {
      const specialtyId = Number(specialtyIdValue);
      filteredPreceptors = filteredPreceptors.filter(p =>
        p.specialty_ids?.includes(specialtyId) ||
        p.specialties?.some(s => Number(s.id) === specialtyId)
      );
    }
    
    const states = filteredPreceptors
      .map(p => p.state)
      .filter((state): state is string => Boolean(state?.trim()));
    const uniqueStates = Array.from(new Set(states)).sort((a, b) => a.localeCompare(b));
    return uniqueStates;
  }, [preceptors, specialtyIdValue]);

  const availableCities = React.useMemo(() => {
    if (!stateValue) return [];
    
    let filteredPreceptors = preceptors;
    
    // Filter by specialty ID if one is selected
    if (specialtyIdValue) {
      const specialtyId = Number(specialtyIdValue);
      filteredPreceptors = filteredPreceptors.filter(p =>
        p.specialty_ids?.includes(specialtyId) ||
        p.specialties?.some(s => Number(s.id) === specialtyId)
      );
    }
    
    // Then filter by state - use exact match with trim
    const citiesInState = filteredPreceptors
      .filter(p => p.state?.toUpperCase().trim() === stateValue.toUpperCase().trim())
      .map(p => p.city)
      .filter((city): city is string => Boolean(city?.trim()));
    const uniqueCities = Array.from(new Set(citiesInState)).sort((a, b) => a.localeCompare(b));
    return uniqueCities;
  }, [stateValue, preceptors, specialtyIdValue]);

  // Sync with external values
  React.useEffect(() => {
    setSpecialtyIdValue(specialtyId);
    setStateValue(state);
    setCityValue(city);
  }, [specialtyId, state, city]);

  const handleSpecialtyChange = (value: string) => {
    setSpecialtyIdValue(value);
    // Clear dependent filters when specialty changes
    setStateValue('');
    setCityValue('');
    onFilterChange(value, '', '');
  };

  const handleStateChange = (value: string) => {
    setStateValue(value);
    // Clear city when state changes
    setCityValue('');
    onFilterChange(specialtyIdValue, value, '');
  };

  const handleCityChange = (value: string) => {
    setCityValue(value);
    onFilterChange(specialtyIdValue, stateValue, value);
  };

  const inline = variant === 'inline';
  const showCity = Boolean(stateValue && availableCities.length > 0);

  // Shared chevron + select styling (compact in inline mode, larger in card mode).
  const chevronStyle: React.CSSProperties = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: inline ? 'right 0.6rem center' : 'right 0.75rem center',
    paddingRight: inline ? '2rem' : '2.5rem',
  };
  const selectClass = inline
    ? 'h-9 w-full min-w-[140px] rounded-lg border border-gray-300 bg-white pl-3 text-sm font-medium text-gray-800 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed'
    : 'w-full h-10 rounded-lg border-2 border-gray-200 bg-white px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed';

  const specialtySelect = (
    <select id="filter-specialty" value={specialtyIdValue} onChange={(e) => handleSpecialtyChange(e.target.value)} disabled={isLoading} className={selectClass} style={chevronStyle}>
      <option value="">All Specialties</option>
      {availableSpecialties.map(spec => (
        <option key={spec.id} value={spec.id}>{spec.name}</option>
      ))}
    </select>
  );
  const stateSelect = (
    <select id="filter-state" value={stateValue} onChange={(e) => handleStateChange(e.target.value)} disabled={isLoading} className={selectClass} style={chevronStyle}>
      <option value="">All States</option>
      {availableStates.map(stateCode => (
        <option key={stateCode} value={stateCode}>{stateCode}</option>
      ))}
    </select>
  );
  const citySelect = (
    <select id="filter-city" value={cityValue} onChange={(e) => handleCityChange(e.target.value)} disabled={isLoading} className={selectClass} style={chevronStyle}>
      <option value="">All Cities</option>
      {availableCities.map(cityName => (
        <option key={cityName} value={cityName}>{cityName}</option>
      ))}
    </select>
  );

  // Inline variant — compact bare selects for the page header (no card/labels).
  if (inline) {
    return (
      <div className="flex items-center gap-2 sm:gap-3">
        {isLoading && (
          <div className="h-4 w-4 shrink-0 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" aria-label="Loading filters" />
        )}
        <div className="flex-1 min-w-0">{specialtySelect}</div>
        <div className="flex-1 min-w-0">{stateSelect}</div>
        {showCity && <div className="flex-1 min-w-0">{citySelect}</div>}
      </div>
    );
  }

  // Card variant (default) — labeled fields inside a bordered card.
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
      {isLoading && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-2">
          <div className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          Loading filters...
        </div>
      )}
      <div className={`grid gap-3 ${showCity ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'} ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider" htmlFor="filter-specialty">Specialty</label>
          {specialtySelect}
        </div>
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider" htmlFor="filter-state">State</label>
          {stateSelect}
        </div>
        {showCity && (
          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider" htmlFor="filter-city">City</label>
            {citySelect}
          </div>
        )}
      </div>
    </div>
  );
}
